import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../contexts/UserContext";

import { GET_ASSUMPTIONS_BY_USER_ID } from "../../apollo-queries";
import { UPDATE_ASSUMPTIONS_BY_USER_ID } from "../../apollo-mutations";
import { useMutation, useQuery } from "@apollo/client";

import { AssumptionsText, NavigationText } from "../../Text";
import { CiCircleInfo } from "react-icons/ci";
import { Button } from "@mui/base";
import Tooltip from "@mui/material/Tooltip";
import { BsArrowLeft, BsArrowRight } from "react-icons/bs";

const AssumptionsForm: React.FC = () => {
  const { user } = useUser();
  const navigate = useNavigate();

  const {
    loading: fetchLoading,
    error: fetchError,
    data: databaseData,
  } = useQuery(GET_ASSUMPTIONS_BY_USER_ID, {
    variables: { user_id: user.uuid },
    fetchPolicy: "network-only", // stops apollo from getting data from the cache and instead queries the DB
  });
  
  const [updateAssumptions, { loading: updateLoading, error: updateError }] =
    useMutation(UPDATE_ASSUMPTIONS_BY_USER_ID);

  // tracks the form data
  const { register, watch, setValue } = useForm({
    defaultValues:
      databaseData !== undefined
        ? databaseData?.assumptions[0]
        : {
            internalHurdleRate: 12,
            description: "",
          },
  });

  // resets the form data and context data to the database data
  useEffect(() => {
    if (databaseData?.assumptions[0]?.internalHurdleRate !== undefined) {
      setValue(
        "internalHurdleRate",
        databaseData?.assumptions[0]?.internalHurdleRate
      );
    } else {
      setValue("internalHurdleRate", 12);
    }
    if (databaseData?.assumptions[0]?.description) {
      setValue("description", databaseData?.assumptions[0]?.description);
    }
  }, [databaseData]); // only runs when databaseData changes

  // updates the database data to the form data on unmount
  useEffect(() => {
    return () => {
      const updatedFormData = watch();
      updateAssumptions({
        variables: {
          user_id: user.uuid,
          internalHurdleRate: updatedFormData.internalHurdleRate,
          description: updatedFormData.description,
        },
      })
        .then((response) => {
          console.log(response);
        })
        .catch((error) => {
          console.log(error);
        });
    };
  }, []); // [] + only returning ensures useEffect only runs on unmount

  const handleNext = () => {
    navigate(`/Review/${user.uuid}`);
  };

  const handlePrevious = () => {
    navigate(`/Qualitative/${user.uuid}`);
  };

  return (
    <>
      <form className="flex flex-col mt-6 p-6 rounded-md bg-white shadow-lg">
        {/* Internal Hurdle Rate */}
        <div>
          <div className="flex flex-col space-y-2 tablet:flex-row tablet:items-center">
            <div className="flex flex-nowrap items-center">
              <label className="text-xl font-semibold whitespace-nowrap">
                {AssumptionsText.internalHurdleRate.label}
                <span className="text-red-500 font-bold ml-1">*</span>
              </label>
              <Tooltip
                title={
                  <>
                    <h3 className="font-bold">
                      {AssumptionsText.internalHurdleRate.label}
                    </h3>
                    <div>{AssumptionsText.internalHurdleRate.tooltip}</div>
                  </>
                }
                className="tablet:hidden"
                arrow
              >
                <Button>
                  <CiCircleInfo className="ml-2" />
                </Button>
              </Tooltip>
            </div>
            <div className="w-20 relative tablet:mr-2">
              <span className="absolute right-2 top-1/2 transform -translate-y-1/2 rl-3 text-UDig-gray">
                %
              </span>
              <input
                type="number"
                min="0"
                max="100"
                maxLength={3}
                {...register("internalHurdleRate")}
                className="text-right w-full h-10 tablet:ml-2 pr-8 border border-light-gray rounded-md"
                required
              />
            </div>
            <Tooltip
              title={
                <>
                  <h3 className="font-bold">
                    {AssumptionsText.internalHurdleRate.label}
                  </h3>
                  <div>{AssumptionsText.internalHurdleRate.tooltip}</div>
                </>
              }
              className="hidden tablet:block"
              placement="right"
              arrow
            >
              <Button>
                <CiCircleInfo className="ml-2" />
              </Button>
            </Tooltip>
          </div>
          <div className="mt-1">
            {AssumptionsText.internalHurdleRate.definition}
          </div>
        </div>
        {/* Description */}
        <div className="mt-2">
          <div className="flex">
            <label className="text-xl font-semibold font-bold">
              {AssumptionsText.description.label}
            </label>
            <Tooltip
              title={
                <>
                  <h3 className="font-bold">
                    {AssumptionsText.description.label}
                  </h3>
                  <div>{AssumptionsText.description.tooltip}</div>
                </>
              }
              placement="right"
              arrow
            >
              <Button>
                <CiCircleInfo className="mx-2 self-center" />
              </Button>
            </Tooltip>
          </div>
          <textarea
            placeholder={AssumptionsText.description.placeholder}
            {...register("description")}
            className="w-full h-20 p-2 mt-2 border border-light-gray rounded-md tablet:w-1/2"
          />
        </div>
      </form>
      {/* Navigation */}
      <div className="mt-4 mb-20 flex justify-between text-UDig-orange">
        <Button
          className="flex items-center gap-x-2"
          type="button"
          onClick={() => {
            handlePrevious();
          }}
        >
          <BsArrowLeft />
          {NavigationText.previous}
        </Button>
        <Button
          className="flex items-center gap-x-2"
          type="button"
          onClick={() => handleNext()}
        >
          {NavigationText.next}
          <BsArrowRight />
        </Button>
      </div>
    </>
  );
};

export default AssumptionsForm;
